import React, { useState } from "react";
import styled, { css } from "styled-components";
import sortBy from "lodash/sortBy";
import {
  typography,
  space,
  color,
  TypographyProps,
  SpaceProps,
  ColorProps
} from "styled-system";
import isFunction from "lodash/isFunction";

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  tr:last-child td {
    border-bottom: 0;
  }
`;

type TdProps = SpaceProps & ColorProps & TypographyProps;

const Td = styled.td<TdProps>`
  font-size: 13px;
  border: 1px solid ${(props) => props.theme.colors.grey1};
  padding: 6px 18px;
  vertical-align: middle;

  & > * {
    vertical-align: middle;
  }

  &:first-child {
    border-left: 0;
  }

  &:last-child {
    border-right: 0;
  }

  ${typography};
  ${space};
  ${color};
`;

Td.defaultProps = {
  fontSize: [1, 2],
  pt: [1, 1, 1],
  pb: [1, 1, 1],
  pl: [1, 1, 2],
  pr: [1, 1, 2]
};

type ThProps = SpaceProps &
  TypographyProps & {
    sortable?: boolean;
  };

const Th = styled.th<ThProps>`
  text-align: left;
  background-color: ${(props) => props.theme.colors.grey0};
  color: ${(props) => props.theme.colors.body};
  padding: 6px 18px;
  ${typography};
  ${space};

  ${(props) =>
    props.sortable &&
    css`
      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
    `};
`;

Th.defaultProps = {
  fontSize: [1, 2],
  pt: [1, 1, 1],
  pb: [1, 1, 1],
  pl: [1, 1, 2],
  pr: [1, 1, 2]
};

const processProps = (props: object, item: any) => {
  if (props == null) {
    return {};
  }

  return Object.entries(props)
    .map(([key, value]) => {
      if (isFunction(value)) {
        return [key, value(item)];
      } else {
        return [key, value];
      }
    })
    .reduce((acc, val) => {
      acc[val[0]] = val[1];
      return acc;
    }, {} as any);
};

type SortableTableProps = {
  columns: any[];
  data: any[];
  defaultSort?: number | null;
  defaultDesc?: boolean;
  emptyMessage?: string;
  disableSort?: boolean;
  rowKey?: string;
  rowStyle?: (item: any) => any;
};

type RowProps = {
  data: any;
  rowKey: string | null | undefined;
  rowStyle: (item: any) => any;
  columns: any[];
};

const Row = React.memo(({ data, rowStyle, columns }: RowProps) => {
  return (
    <tr style={rowStyle(data)}>
      {columns.map((c, i) => (
        <Td key={i} {...processProps(c.props, data)}>
          {c.format ? c.format(data) : data[c.name]}
        </Td>
      ))}
    </tr>
  );
});

const SortableTable = ({
  columns,
  data,
  rowKey,
  defaultSort = null,
  defaultDesc = false,
  emptyMessage = "",
  disableSort = false,
  rowStyle = () => ({})
}: SortableTableProps) => {
  const [sort, setSort] = useState<number>(defaultSort || 0);
  const [sortDir, setSortDir] = useState<boolean>(defaultDesc || false);

  const doSort = (index: number) => {
    if (sort === index) {
      setSortDir((x) => !x);
    } else {
      setSortDir(true);
      setSort(index);
    }
  };

  const sortedColumn = columns[sort];

  const sortKey = sortedColumn.sortFormat || sortedColumn.name;

  const sorted = sortBy(data, sortKey);

  if (sortDir) {
    sorted.reverse();
  }

  return (
    <Table>
      <thead>
        <tr>
          {columns.map(({ label, headingProps, notSortable }, i) => (
            <Th
              sortable={!disableSort && !notSortable}
              key={i}
              {...(headingProps || {})}
              onClick={() => !disableSort && !notSortable && doSort(i)}
            >
              {label}
              {sort === i && (sortDir ? " ▼" : " ▲")}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((data, i) => (
          <Row
            key={rowKey ? data[rowKey] : i}
            data={data}
            rowStyle={rowStyle}
            columns={columns}
            rowKey={rowKey}
          />
        ))}
        {sorted.length === 0 && (
          <tr>
            <Td colSpan={columns.length} textAlign="center">
              {emptyMessage}
            </Td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

export default SortableTable;
